import {
  ACCESSIBILITY_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_MENU_ARGS,
  MENU_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { MENU_ITEM_SELECTOR } from '../../../../utils/spds/constants/menu';
import { ROLE_LISTBOX } from '../../../../utils/spds/constants/aria';
import { applyBoldMatchingText, stripBolding } from '../../../../modifiers/bold-matching-text';
import { filterMenuItemsByText } from '../../../../utils/spds/menu-filter';
import { fn } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { task, timeout } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { waitForNext } from '../../../../utils/waiters';
import FlexColumn from '../../layout/flex/column.gjs';
import MenuSearch from './search.gjs';
import NoResults from './no-results.gjs';
import Super from '../../core/base.gjs';
import VariableLayout from '../../layout/variable.gjs';
import styles from './base.module.scss';

const SEARCH_DEBOUNCE_MS = 200;

const METADATA = Metadata.define({
  description: `
Simple, easy to use menu component.

### Positioning

Providing the \`@anchorElement\` argument as a DOM element that is used to compute the \`top\` property for the menu. This can be
further modified by providing the \`@anchorOffset\` argument, which adjusts the distance between the top of the menu and the bottom of
the anchor element. Left/right alignment is determined by the width of the menu and the horizontal positioning of the anchor. If the anchor
position plus the menu width extends beyond the right edge of the viewport, the menu will automatically align to the right side of the anchor.
Otherwise, the menu defaults to aligning to the left side of the anchor. In either case, the position of the non-aligned side will be determined
in one of two ways:

* Default behavior causes the menu to flex its width to fit the container of the anchor element
* If the \`@fitContent\` argument is provided, the menu will restrict its width to the required space to fit its largest content

### In-menu search

When a menu renders at least \`@searchThreshold\` items (15 by default), a search field is automatically added to the top of the menu that
filters the items by text as the user types. This can be disabled per-menu by passing \`@searchable={{false}}\`, or forced on regardless of the
item count with \`@searchable={{true}}\`. When the active filter matches no items, a no-results message (customizable via \`@noResultsText\`) is shown.
  `,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_LISTBOX }, 'ariaLabelledBy']),
    pick(COMPONENT_STATE_ARGS, ['open', { name: 'animated', default: true }]),
    pick(CONTAINER_ARGS, ['fitContent', { name: 'column', default: true }]),
    pick(MENU_ARGS, ['anchorElement', { name: 'anchorOffset', default: 4 }]),
    pick(INPUT_MENU_ARGS, [
      'multiselect',
      'noItemsDisplayed',
      'noResultsText',
      'searchable',
      'searchPlaceholder',
      'searchThreshold',
    ]),
  ],
  name: 'Menu Base',
});

export default class SpdsCoreMenuBase extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu';
  classesToMap = ['animated', 'open', 'side'];
  propsToMap = ['top'];
  styles = styles;

  @tracked menuElement;
  @tracked side = 'left';
  @tracked itemCount = 0;
  @tracked listComponent;
  @tracked searchComponent;
  @tracked searchValue = '';
  @tracked top;
  @tracked visibleCount = 0;

  get anchorBoundingRect() {
    return this.args.anchorElement?.getBoundingClientRect();
  }

  get anchorDistance() {
    return this.args.anchorOffset ?? this.constructor.metadata.getDefault('anchorOffset');
  }

  get animated() {
    return this.argOrDefault('animated');
  }

  get canBePositioned() {
    return !!this.args.anchorElement && !!this.baseElement;
  }

  get column() {
    return this.argOrDefault('column');
  }

  get listId() {
    return this.composeSubcomponentId('list');
  }

  get parentBoundingRect() {
    return this.args.anchorElement?.parentElement?.getBoundingClientRect();
  }

  get itemElements() {
    if (!this.baseElement) return [];

    return Array.from(this.listComponent?.baseElement?.querySelectorAll(MENU_ITEM_SELECTOR) ?? []);
  }

  get role() {
    return this.argOrDefault('role');
  }

  get searchableIsSet() {
    let { searchable } = this.args;

    return searchable === Boolean(searchable);
  }

  get searchId() {
    return this.composeSubcomponentId('search');
  }

  get searchInputElement() {
    return this.searchComponent?.inputElement;
  }

  get searchThreshold() {
    return this.argOrDefault('searchThreshold');
  }

  get searchEnabled() {
    if (this.searchableIsSet) return this.args.searchable;

    return this.itemCount >= this.searchThreshold;
  }

  get searchActive() {
    return this.searchEnabled && this.searchValue.length > 0;
  }

  get showNoResults() {
    if (this.args.noItemsDisplayed) return true;

    return this.searchActive && this.visibleCount === 0;
  }

  @(task(function* () {
    yield timeout(SEARCH_DEBOUNCE_MS);
    this.runFilter();
  }).restartable())
  debouncedFilter;

  setMenuPosition = modifier(() => {
    if (!this.canBePositioned) return;

    let anchorBottom = this.anchorBoundingRect?.bottom;
    let menuRight = this.anchorBoundingRect?.x + this.baseElement?.getBoundingClientRect()?.width;
    let parentTop = this.parentBoundingRect?.top || 0;
    let top = anchorBottom - parentTop + this.anchorDistance;

    this.side = menuRight > window.innerWidth ? 'right' : 'left';
    this.top = `${top}px`;
  });

  syncSearchToOpenState = modifier((element, [open]) => {
    if (!open) {
      this.resetSearch();
      return;
    }

    if (this.searchEnabled) this.searchInputElement?.focus();
  });

  runFilter() {
    let value = this.searchValue;
    let multiselect = !!this.args.multiselect;
    let items = this.itemElements;
    let matches = filterMenuItemsByText(items, value, { multiselect });

    items.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      if (!display) return;

      if (value) {
        applyBoldMatchingText(display, value);
      } else {
        stripBolding(display.querySelector('span') ?? display);
      }
    });

    this.visibleCount = matches.length;
  }

  setup() {
    this.itemCount = this.itemElements.length;
  }

  resetSearch = async () => {
    this.debouncedFilter.cancelAll();
    if (!this.searchValue) return;

    await waitForNext();

    if (this.isDestroying || this.isDestroyed) return;

    this.searchValue = '';
    this.runFilter();
  };

  clearSearch = () => {
    this.resetSearch();
    this.searchInputElement?.focus();
  };

  onSearchInput = evt => {
    this.searchValue = evt?.target?.value ?? '';
    this.debouncedFilter.perform();
  };

  <template>
    <FlexColumn
      @absolute={{true}}
      @fitContentWidth={{@fitContent}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu
      id={{this.id}}
      style={{this.customProperties}}
      {{this.setupComponent}}
      {{this.setMenuPosition}}
      {{this.syncSearchToOpenState @open}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if this.searchEnabled}}
        <MenuSearch
          @ariaControls={{this.listId}}
          @flex="1"
          @id={{this.searchId}}
          @onClear={{this.clearSearch}}
          @onInput={{this.onSearchInput}}
          @placeholder={{@searchPlaceholder}}
          @registerComponent={{fn this.registerComponent "searchComponent"}}
          @unregisterComponent={{fn this.unregisterComponent "searchComponent"}}
          @value={{this.searchValue}}
        />
      {{/if}}

      <VariableLayout
        @column={{this.column}}
        @gap="4px"
        @id={{this.listId}}
        @registerComponent={{fn this.registerComponent "listComponent"}}
        @unregisterComponent={{fn this.unregisterComponent "listComponent"}}
        aria-labelledby={{@ariaLabelledBy}}
        class="spds-menu-list"
        data-test-spds-menu-list
        role={{this.role}}
      >
        {{yield}}

        {{#if this.showNoResults}}
          <NoResults @text={{@noResultsText}} />
        {{/if}}
      </VariableLayout>
    </FlexColumn>
  </template>
}

export const SPDS_CORE_MENU_BASE_METADATA = SpdsCoreMenuBase.metadata;
